import React, { useState } from "react";
import Papa from "papaparse";
import { useGuideFlowProviderContext } from "../../../../GuideFlowProviderContext";
import { Button } from "../../../controls/tailwindui/button";

interface ParsedContact {
    firstname: string;
    surname: string;
    email: string;
    phone: string;
    mmsStudentNumber: string;
}

export default function ImportContactsTab() {
    const context = useGuideFlowProviderContext();
    const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([]);
    const [toAdd, setToAdd] = useState<ParsedContact[]>([]);
    const [toUpdate, setToUpdate] = useState<ParsedContact[]>([]);
    const [allContacts, setAllContacts] = useState<any[]>([]);

    const [isSaving, setIsSaving] = useState(false);
    const [progress, setProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState("");
    const [results, setResults] = useState<{ added: number; updated: number } | null>(null);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;

        const file = e.target.files[0];
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                const rows = results.data as any[];
                const contacts: ParsedContact[] = rows.map((row) => ({
                    firstname: row["firstname"]?.trim() || "",
                    surname: row["surname"]?.trim() || "",
                    email: row["email"]?.trim() || "",
                    phone: row["phone"]?.trim() || "",
                    mmsStudentNumber: row["mms_student_number"]?.trim() || "",
                }));

                const existing = await context.Contacts.getAll();
                setAllContacts(existing);

                const updates: ParsedContact[] = [];
                const adds: ParsedContact[] = [];

                contacts.forEach((c) => {
                    const match = existing.find(
                        (ec) => ec.email.toLowerCase() === c.email.toLowerCase()
                    );
                    if (match) {
                        updates.push(c);
                    } else {
                        adds.push(c);
                    }
                });

                setParsedContacts(contacts);
                setToAdd(adds);
                setToUpdate(updates);
                setResults(null); // clear any previous results
            },
        });
    };

    const handleSave = async () => {
        setIsSaving(true);
        setProgress(0);
        setStatusMessage("Starting import…");

        for (let i = 0; i < parsedContacts.length; i++) {
            const c = parsedContacts[i];
            setStatusMessage(
                `Importing ${c.firstname} ${c.surname} (${i + 1}/${parsedContacts.length})…`
            );

            const existing = allContacts.find(
                (ec) => ec.email.toLowerCase() === c.email.toLowerCase()
            );

            const data = {
                ...existing,
                id: existing?.id ?? crypto.randomUUID(),
                firstname: c.firstname,
                surname: c.surname,
                email: c.email,
                phone: c.phone,
                mmsStudentNumber: c.mmsStudentNumber,
                dateOfBirth: existing?.dateOfBirth ?? Date.now(),
            };

            await context.Contacts.save(data);
            setProgress(((i + 1) / parsedContacts.length) * 100);
        }

        setStatusMessage("All contacts imported successfully!");
        setIsSaving(false);
        setResults({ added: toAdd.length, updated: toUpdate.length });
    };

    return (
        <div className="space-y-4 m-4">
            <h2 className="text-lg font-semibold">Upload Contacts CSV</h2>
            <p className="text-sm text-gray-600">
                CSV must contain the following **columns in this order** (case-sensitive):<br />
                <code>firstname, surname, email, phone, mms_student_number</code>
            </p>

            <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="block"
            />

            {/* Preview before saving */}
            {parsedContacts.length > 0 && !isSaving && !results && (
                <div className="space-y-6">
                    <div>
                        <h3 className="font-medium">Contacts to Add ({toAdd.length})</h3>
                        <table className="min-w-full border mt-2 text-sm">
                            <thead className="bg-gray-100">
                            <tr>
                                <th className="px-2 py-1 border">Firstname</th>
                                <th className="px-2 py-1 border">Surname</th>
                                <th className="px-2 py-1 border">Email</th>
                                <th className="px-2 py-1 border">Phone</th>
                                <th className="px-2 py-1 border">Student #</th>
                            </tr>
                            </thead>
                            <tbody>
                            {toAdd.map((c, idx) => (
                                <tr key={idx}>
                                    <td className="px-2 py-1 border">{c.firstname}</td>
                                    <td className="px-2 py-1 border">{c.surname}</td>
                                    <td className="px-2 py-1 border">{c.email}</td>
                                    <td className="px-2 py-1 border">{c.phone}</td>
                                    <td className="px-2 py-1 border">{c.mmsStudentNumber}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    <div>
                        <h3 className="font-medium">Contacts to Update ({toUpdate.length})</h3>
                        <table className="min-w-full border mt-2 text-sm">
                            <thead className="bg-gray-100">
                            <tr>
                                <th className="px-2 py-1 border">Firstname</th>
                                <th className="px-2 py-1 border">Surname</th>
                                <th className="px-2 py-1 border">Email</th>
                                <th className="px-2 py-1 border">Phone</th>
                                <th className="px-2 py-1 border">Student #</th>
                            </tr>
                            </thead>
                            <tbody>
                            {toUpdate.map((c, idx) => (
                                <tr key={idx}>
                                    <td className="px-2 py-1 border">{c.firstname}</td>
                                    <td className="px-2 py-1 border">{c.surname}</td>
                                    <td className="px-2 py-1 border">{c.email}</td>
                                    <td className="px-2 py-1 border">{c.phone}</td>
                                    <td className="px-2 py-1 border">{c.mmsStudentNumber}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    <Button onClick={handleSave}>Confirm & Save</Button>
                </div>
            )}

            {/* Import in progress */}
            {isSaving && (
                <div className="p-4 border rounded bg-gray-50 space-y-2">
                    <p className="font-medium">{statusMessage}</p>
                    <div className="w-full bg-gray-200 h-3 rounded">
                        <div
                            className="h-3 bg-blue-500 rounded transition-all"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <p className="text-sm text-gray-600">{Math.round(progress)}%</p>
                </div>
            )}

            {/* Results report */}
            {results && !isSaving && (
                <div className="p-4 border rounded bg-green-50 space-y-2">
                    <h3 className="font-medium text-green-800">Import Completed</h3>
                    <p>{results.added} new contacts added</p>
                    <p>{results.updated} existing contacts updated</p>
                </div>
            )}
        </div>
    );
}
